/**
 * SA-1422 (spec §4.5): which account's inbox an email mailbox reads. The
 * field is prefilled with the account a refusal offered, else the typed
 * address, else the connected account; Continue waits for an address and
 * hands it, trimmed, to the page, which starts the connect. The words live in
 * `connectedAccountText.ts` and `mailboxesText.ts`.
 */
import { useState } from 'react'
import {
  Button, Input, Modal, ModalBody, ModalContent, ModalFooter, ModalHeader, ModalTitle,
} from '@/components/ui'
import type { Mailbox } from '@/services/mailboxes'
import { isAddressShaped } from './connectErrorText'
import { changeAccountLines } from './connectedAccountText'
import { ACCOUNT_FIELD, CANCEL, CHANGE_ACCOUNT_TITLE, CONTINUE } from './mailboxesText'

interface ChangeAccountDialogProps {
  mailbox: Mailbox
  open: boolean
  onOpenChange: (open: boolean) => void
  /** The account a refusal offered; it wins over the mailbox's own addresses. */
  initialAccount?: string
  onContinue: (account: string) => void
}

export function ChangeAccountDialog({
  mailbox, open, onOpenChange, initialAccount, onContinue,
}: ChangeAccountDialogProps) {
  const [account, setAccount] = useState(
    () => initialAccount?.trim() || mailbox.email_address?.trim() || mailbox.connected_account?.trim() || '',
  )
  const trimmed = account.trim()
  const ready = isAddressShaped(trimmed)

  return (
    <Modal open={open} onOpenChange={onOpenChange}>
      <ModalContent size="md">
        <ModalHeader>
          <ModalTitle>{CHANGE_ACCOUNT_TITLE}</ModalTitle>
        </ModalHeader>
        <ModalBody className="space-y-4">
          <Input
            id={`change-account-${mailbox.id}`}
            label={ACCOUNT_FIELD}
            type="text"
            inputMode="email"
            autoComplete="off"
            autoCapitalize="none"
            spellCheck={false}
            value={account}
            onChange={(e) => setAccount(e.target.value)}
          />
          <div className="space-y-2">
            {changeAccountLines(mailbox).map((line) => (
              <p key={line} className="text-sm text-muted-foreground">{line}</p>
            ))}
          </div>
        </ModalBody>
        <ModalFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>{CANCEL}</Button>
          <Button onClick={() => onContinue(trimmed)} disabled={!ready}>{CONTINUE}</Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}
